import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Platform } from '@ionic/angular';

@Directive({
  selector: '[appResizableColumn]',
})
export class TableAndColumnSizeDirective
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() mode: 'fullscreen' | 'scroll' = 'fullscreen';

  matTableRef: ElementRef;
  private subscription = new Subscription();
  private pressed = false;
  private firstMove = true;
  private startX = 0;
  private baseIndex = 0;
  private columns = [];
  private prevMouseX = 0;
  private destroy$: Subject<void> = new Subject<void>();
  private isResizingRight = false;
  private minWidth = 30;
  private thresholdX = 0;
  private thresholdRight = false;
  private initialRows = 0;
  private checkCounter = 0;

  constructor(
    el: ElementRef,
    private renderer: Renderer2,
    private platform: Platform
  ) {
    this.matTableRef = el;
  }

  @HostListener('window:resize', ['$event']) onResize(event) {
    setTimeout(() => {
      this.setTableResize();
      this.updateColumns();
    }, 100);
  }

  @HostListener('document:mousemove', ['$event']) onMouseMove(event) {
    if (this.pressed) {
      this.checkResizing(event, this.baseIndex);

      if (this.thresholdX !== 0) {
        if (
          (this.thresholdRight && event.pageX > this.thresholdX) ||
          (!this.thresholdRight && event.pageX < this.thresholdX)
        ) {
          // console.log(event.pageX);
          // console.log(this.thresholdX - 4);

          this.startX = this.thresholdX;
          this.thresholdX = 0;
        } else {
          this.startX = event.pageX;
        }
      }

      // Update columns
      const dx = event.pageX - this.startX;
      // console.log("dx", dx);

      const baseColumn = this.getColumn(this.baseIndex);
      const newWidth = this.getElementWidth(baseColumn) + dx;

      const neighbourColumn = this.getColumn(this.baseIndex + 1);
      const newWidthNeighbour = this.getElementWidth(neighbourColumn) + dx * -1;

      const thresholdReached =
        newWidthNeighbour < this.minWidth || newWidth < this.minWidth;
      const totalWidth = this.getElementWidth(this.matTableRef.nativeElement);

      // console.log('baseColumn', newWidth);
      // console.log('newWidthNeighbour', newWidthNeighbour);
      // console.log('totalWidth', totalWidth);

      if (!thresholdReached) {
        if (this.mode === 'fullscreen') {
          const newWidthPercentage = (newWidth / totalWidth) * 100;
          const newWidthNeighbourPercentage =
            (newWidthNeighbour / totalWidth) * 100;

          this.renderer.setStyle(baseColumn, 'width', newWidthPercentage + '%');
          this.renderer.setStyle(
            neighbourColumn,
            'width',
            newWidthNeighbourPercentage + '%'
          );
        } else {
          // Set the table width to match
          this.renderer.setStyle(
            this.matTableRef.nativeElement,
            'width',
            totalWidth + 'px'
          );

          this.renderer.setStyle(baseColumn, 'width', newWidth + 'px');
          this.renderer.setStyle(
            neighbourColumn,
            'width',
            newWidthNeighbour + 'px'
          );
        }
      } else {
        if (this.thresholdX === 0) {
          if (newWidthNeighbour < this.minWidth) {
            this.thresholdX =
              event.pageX +
              dx * -1 -
              (this.getElementWidth(neighbourColumn) - this.minWidth);
            this.thresholdRight = false;
          }
          if (newWidth < this.minWidth) {
            this.thresholdX =
              event.pageX +
              dx -
              (this.getElementWidth(baseColumn) - this.minWidth);
            this.thresholdRight = true;
          }
        }
      }
      this.startX = event.pageX;
    }
  }

  @HostListener('document:touchmove', ['$event']) onTouchStart(event) {
    this.onMouseMove(event.changedTouches[0]);
  }

  @HostListener('document:mouseup', ['$event']) onMouseUp(event) {
    this.pressed = false;
    this.firstMove = true;
    this.thresholdX = 0;
    this.updateColumns();
  }

  @HostListener('document:touchend', ['$event']) onTouchStop(event) {
    this.onMouseUp(event);
  }

  ngOnInit() {
    this.renderer.setStyle(
      this.matTableRef.nativeElement,
      'table-layout',
      'fixed'
    );
    this.renderer.addClass(this.matTableRef.nativeElement, this.mode);
  }

  ngAfterViewInit() {
    this.updateColumns();
    this.initialRows = this.matTableRef.nativeElement?.rows.length;
    setTimeout(() => this.checkFirstRows(), 250);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkFirstRows() {
    if (this.matTableRef.nativeElement?.rows.length > this.initialRows) {
      this.setTableResize();
      return;
    }

    if (this.checkCounter < 15) {
      this.checkCounter += 1;
      setTimeout(() => this.checkFirstRows(), 250);
    }
  }

  getElementWidth(element) {
    return element.offsetWidth;
  }

  updateColumns() {
    this.columns = [];
    const headerRows = this.matTableRef.nativeElement?.rows[0];
    if (headerRows) {
      for (let i = 0; i < headerRows.cells.length; i++) {
        this.columns.push({
          index: i,
        });

        // Check if the resize handler already exists
        let resizeHandle = headerRows.cells[i].querySelector('.resize-handler');

        // If the resize handler doesn't exist, create and append it
        if (!resizeHandle && headerRows.cells.length - 1 !== i) {
          resizeHandle = document.createElement('span');
          resizeHandle.classList.add('resize-handler');
          resizeHandle.addEventListener('mousedown', (event) => {
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.onResizeColumn(event, i);
          });
          resizeHandle.addEventListener('touchstart', (event) => {
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.onResizeColumn(event, i);
          });

          resizeHandle.addEventListener('click', (event) => {
            event.stopPropagation();
            event.stopImmediatePropagation();
          });

          resizeHandle.addEventListener('matSortChange', (event) => {
            event.stopPropagation();
            event.stopImmediatePropagation();
          });

          headerRows.cells[i].appendChild(resizeHandle);
        }
      }
    }
  }

  getColumn(index: number) {
    return this.matTableRef.nativeElement?.rows[0]?.cells[index];
  }

  setTableResize() {
    const newWidth = this.getTableWidth();
    const rows = this.matTableRef.nativeElement.rows;

    // console.log("Table size", rows);

    const maxLengths = [];

    for (let i = 0; i < rows.length; i++) {
      const cells = rows[i].cells;
      for (let j = 0; j < cells.length; j++) {
        let textLength = 0;
        try {
          const font = window
            .getComputedStyle(cells[j])
            .getPropertyValue('font');
          textLength = this.getTextWidth(cells[j].textContent, font);
        } catch (e) {
          console.error(e);
          textLength = 40;
        }

        if (textLength < 40) {
          textLength = 40;
        }

        if (!maxLengths[j] || textLength > maxLengths[j]) {
          textLength = textLength + 25;
          if (j === cells.length - 1) {
            textLength += 95;
          }
          maxLengths[j] = textLength;
        }
      }
    }
    // console.log(this.mode);
    // console.log(maxLengths, newWidth);
    if (this.mode === 'fullscreen') {
      this.resizeForFullscreen(maxLengths, newWidth);
    } else {
      this.resizeForScroll(maxLengths);
    }
  }

  resizeForScroll(maxLengths: number[]) {
    try{
      const maxElementLength = 300;
      let maxLength = 0;

      for (let i = 0; i < maxLengths.length; i++) {
        if (i === maxLengths.length - 1) {
          maxLengths[i] += 75;
        } else {
          maxLengths[i] += 25;
        }

        if (maxElementLength < maxLengths[i]) {
          maxLengths[i] = maxElementLength;
        }

        maxLength = maxLength + maxLengths[i];
      }

      this.renderer.setStyle(
        this.matTableRef.nativeElement,
        'width',
        maxLength + 'px'
      );

      // console.log('widht', maxLength);

      for (let i = 0; i < maxLengths.length; i++) {
        const column = this.getColumn(i);
        this.renderer.setStyle(column, 'width', maxLengths[i] + 'px');
      }
    }catch(e){
      console.error(e);
    }
  }

  resizeForFullscreen(maxLengths: number[], newWidth: number) {
    try {
      let maxLength = 0;
      for (const element of maxLengths) {
        maxLength = maxLength + element;
      }

      while (maxLength > newWidth) {
        const widestIndex = maxLengths.indexOf(Math.max(...maxLengths));
        maxLengths[widestIndex] = maxLengths[widestIndex] - 1;
        maxLength = maxLength - 1;
      }

      let i = 0;
      while (maxLength < newWidth) {
        maxLengths[i] = maxLengths[i] + 1;
        maxLength = maxLength + 1;
        if (i < this.columns.length - 1) {
          i++;
        } else {
          i = 0;
        }
      }
      let sum = 0;
      for (let i = 0; i < maxLengths.length; i++) {
        const column = this.getColumn(i);
        let percentageWidth = (maxLengths[i] / newWidth) * 100;
        if (i === maxLengths.length - 1) {
          percentageWidth = 100 - sum;
        }
        sum += percentageWidth;
        // console.log(percentageWidth);

        this.renderer.setStyle(column, 'width', percentageWidth + '%');
      }
    } catch (e) {
      console.error(e);
    }

  }

  getTextWidth(text, font) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = font;
    const width = ctx.measureText(text).width;
    canvas.remove();
    return width;
  }

  onResizeColumn(event: any, index: number) {
    this.pressed = true;

    if (event.pageX) {
      this.startX = event.pageX;
    } else {
      this.startX = event.changedTouches[0].pageX;
    }
    this.prevMouseX = this.startX;
    this.baseIndex = index;
  }

  private getTableWidth(): number {
    return this.matTableRef.nativeElement.clientWidth;
  }

  private checkResizing(event, index) {
    const mouseX = event.clientX;
    const diffX = mouseX - this.prevMouseX;
    this.isResizingRight = diffX > 0;
    this.prevMouseX = mouseX;
  }
}
